/**
 * /vendre — « Vendez votre voiture » (2026-09-16).
 *
 * L'ESTIMATION EST MONTRÉE AVANT QU'ON DEMANDE QUOI QUE CE SOIT. C'est tout
 * l'angle : la concurrence affiche un chiffre d'appât pour récupérer un
 * numéro de téléphone (une 206 « estimée » 2 540 €, rachetée 65 €). Ici le
 * chiffre est celui du moteur à quatre niveaux, avec son niveau de confiance,
 * et la reprise est distinguée du prix entre particuliers.
 *
 * Quatre étapes : le véhicule, l'estimation, les coordonnées, la confirmation.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Page from '../components/Layout/Page';
import BandeauAffaires from '../components/Affaires/BandeauAffaires';
import { LIBELLES_NIVEAUX } from '../components/Affaires/VerdictPrix';
import { joli, nombre } from '../components/Affaires/format';
import api from '../services/api';
import './VendrePage.css';

const ENERGIES = ['Essence', 'Diesel', 'Hybride', 'Électrique', 'GPL'];
const BOITES = ['Manuelle', 'Automatique'];
const ETATS = [
  ['tres_bon', 'Très bon état'],
  ['bon', 'Bon état'],
  ['frais', 'Frais à prévoir'],
  ['hs', 'Ne roule pas'],
];
const VEHICULE_VIDE = { marque: '', modele: '', annee: '', kilometrage: '', energie: '', boite: '' };
// ⚠️ Le champ leurre ne s'appelle PAS « website », « url », « company » ni
// « address » : ce sont des noms que les navigateurs remplissent tout seuls,
// et un vendeur dont l'autofill remplit le leurre voit son dépôt jeté en
// silence. Nom neutre, aucun attribut `name`, et rien que le navigateur
// puisse rattacher à une donnée connue.
const CONTACT_VIDE = { prenom: '', telephone: '', email: '', code_postal: '', etat_general: '', prix_souhaite: '', description: '', piege: '' };

const message = (err, repli) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, '');
  return repli;
};

function Etapes({ etape }) {
  const noms = ['Le véhicule', 'L’estimation', 'Vos coordonnées'];
  return (
    <ol className="zk-vnd-etapes" aria-label="Étapes">
      {noms.map((nom, i) => (
        <li key={nom} className={etape === i + 1 ? 'on' : etape > i + 1 ? 'fait' : ''} aria-current={etape === i + 1 ? 'step' : undefined}>
          <span>{i + 1}</span>{nom}
        </li>
      ))}
    </ol>
  );
}

function Estimation({ e, onSuite, onRetour }) {
  const niveau = e.niveau;
  if (!niveau) {
    return (
      <section className="zk-carte zk-vnd-estimation">
        <h2>Nous ne pouvons pas estimer cette voiture</h2>
        <p className="zk-vnd-muet">
          Nous n’avons pas assez de voitures comparables en vente pour avancer un chiffre que nous puissions défendre.
          Plutôt qu’une estimation inventée, nous préférons vous le dire. Laissez-nous vos coordonnées : nous estimons
          à la main et nous vous rappelons.
        </p>
        <div className="zk-vnd-actions">
          <button type="button" className="zk-bouton" onClick={onSuite}>Être rappelé</button>
          <button type="button" className="zk-lien" onClick={onRetour}>Modifier le véhicule</button>
        </div>
      </section>
    );
  }
  const approx = niveau === 'INDICATIVE' || niveau === 'ESTIMATION' ? '≈ ' : '';
  return (
    <section className="zk-carte zk-vnd-estimation">
      <p className={`zk-vp-badge ${niveau.toLowerCase()}`}>
        <b>{LIBELLES_NIVEAUX[niveau]}</b>
        {e.comparables > 0 && <span> · {e.comparables} voitures identiques en vente</span>}
        {!e.comparables && e.groupe_taille > 0 && <span> · {e.groupe_taille} voitures comparées</span>}
      </p>

      <div className="zk-vnd-prix">
        <p className="zk-vnd-libelle">Prix de vente entre particuliers</p>
        <p className="zk-vnd-montant">{approx}{nombre(e.prix_particulier)} €</p>
        {e.particulier_bas != null && e.particulier_haut != null && (
          <p className="zk-vnd-muet">le plus souvent entre {nombre(e.particulier_bas)} € et {nombre(e.particulier_haut)} €</p>
        )}
      </div>

      <div className="zk-vnd-reprise">
        <p className="zk-vnd-libelle">Reprise par un garage</p>
        <p className="zk-vnd-montant">entre {nombre(e.reprise_bas)} € et {nombre(e.reprise_haut)} €</p>
        <p className="zk-vnd-muet">
          Un professionnel rachète moins cher, mais c’est immédiat et sans démarches : pas d’annonce, pas de visites,
          pas de paiement à sécuriser.
        </p>
      </div>

      <details className="zk-vnd-pourquoi">
        <summary>pourquoi ces chiffres ?</summary>
        <p>
          Le prix entre particuliers vient des voitures comparables réellement en vente (même modèle, motorisation,
          année proche, kilométrage proche). Les annonces de professionnels y sont ramenées au niveau particulier :
          mesuré sur notre base, un pro affiche {e.hypotheses?.prime_pro_pct ?? 2}&nbsp;% de plus sur une voiture
          comme la vôtre.
        </p>
        <p>
          La reprise part du prix auquel un garage revendra la voiture, moins sa marge
          ({e.hypotheses?.marge_pct?.[0] ?? 10} à {e.hypotheses?.marge_pct?.[1] ?? 18}&nbsp;%) et ses frais fixes de
          préparation, contrôle et garantie ({nombre(e.hypotheses?.frais_fixes?.[0] ?? 600)} à{' '}
          {nombre(e.hypotheses?.frais_fixes?.[1] ?? 1200)} €). Ces deux paramètres sont des hypothèses : aucune
          annonce ne publie un prix de rachat. L’offre ferme viendra du garage, après avoir vu la voiture.
        </p>
      </details>

      <div className="zk-vnd-actions">
        <button type="button" className="zk-bouton plein" onClick={onSuite}>Recevoir des offres de garages</button>
        <button type="button" className="zk-lien" onClick={onRetour}>Modifier le véhicule</button>
      </div>
    </section>
  );
}

export default function VendrePage() {
  const [etape, setEtape] = useState(1);
  const [vehicule, setVehicule] = useState(VEHICULE_VIDE);
  const [contact, setContact] = useState(CONTACT_VIDE);
  const [photos, setPhotos] = useState([]);
  const [consentement, setConsentement] = useState(false);
  const [estimation, setEstimation] = useState(null);
  const [depot, setDepot] = useState(null);
  const [marques, setMarques] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [correction, setCorrection] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    api.get('/api/verifier/marques').then((r) => setMarques(r.data?.marques || [])).catch(() => {});
  }, []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [etape]);

  const majVehicule = (k) => (e) => setVehicule((v) => ({ ...v, [k]: e.target.value }));
  const majContact = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  async function verifierMarque() {
    const saisie = vehicule.marque.trim();
    if (saisie.length < 2) return;
    try {
      const { data } = await api.get('/api/verifier/modeles', { params: { marque: saisie } });
      setModeles(data?.modeles || []);
      if (data?.marque && data.marque.toLowerCase() !== saisie.toLowerCase()) {
        setVehicule((v) => ({ ...v, marque: data.marque }));
        setCorrection(`« ${saisie} » corrigé en ${data.marque}`);
      } else {
        setCorrection('');
      }
    } catch { /* le serveur corrigera à l'envoi */ }
  }

  async function estimer(e) {
    e.preventDefault();
    setOccupe(true);
    setErreur(null);
    try {
      const { data } = await api.post('/api/vente/estimer', {
        marque: vehicule.marque.trim(),
        modele: vehicule.modele.trim(),
        annee: Number(vehicule.annee),
        kilometrage: Number(vehicule.kilometrage),
        energie: vehicule.energie || null,
        boite: vehicule.boite || null,
      });
      setEstimation(data);
      setEtape(2);
    } catch (err) {
      setErreur(message(err, "L'estimation n'a pas abouti. Réessayez dans un moment."));
    } finally {
      setOccupe(false);
    }
  }

  async function deposer(e) {
    e.preventDefault();
    setOccupe(true);
    setErreur(null);
    try {
      const { data } = await api.post('/api/vente/depots', {
        marque: vehicule.marque.trim(),
        modele: vehicule.modele.trim(),
        annee: Number(vehicule.annee),
        kilometrage: Number(vehicule.kilometrage),
        energie: vehicule.energie || null,
        boite: vehicule.boite || null,
        prenom: contact.prenom.trim(),
        telephone: contact.telephone.trim(),
        email: contact.email.trim(),
        code_postal: contact.code_postal.trim(),
        etat_general: contact.etat_general || null,
        prix_souhaite: contact.prix_souhaite ? Number(contact.prix_souhaite) : null,
        description: contact.description.trim() || null,
        consentement_garages: consentement,
        piege: contact.piege,
      });
      if (data.id && photos.length) {
        const corps = new FormData();
        corps.append('jeton', data.jeton_photos);
        photos.slice(0, data.photos_max || 5).forEach((f) => corps.append('fichiers', f));
        await api.post(`/api/vente/depots/${data.id}/photos`, corps).catch(() => {});
      }
      setDepot(data);
      setEtape(4);
    } catch (err) {
      setErreur(message(err, "Votre demande n'a pas pu être envoyée. Réessayez dans un moment."));
    } finally {
      setOccupe(false);
    }
  }

  const souhaite = Number(contact.prix_souhaite);
  const ecartSouhaite = souhaite > 0 && estimation?.prix_particulier
    ? Math.round(((souhaite - estimation.prix_particulier) / estimation.prix_particulier) * 100)
    : null;

  return (
    <Page
      largeur="etroite"
      titre="Vendez votre voiture"
      chapeau="Une estimation honnête, puis des offres de garages près de chez vous."
    >
      {etape < 4 && <Etapes etape={etape} />}
      {erreur && <div role="alert" className="zk-alerte erreur zk-vnd-bloc">{erreur}</div>}

      {etape === 1 && (
        <form className="zk-carte zk-vnd-form" onSubmit={estimer}>
          <div className="zk-vnd-grille">
            <div className="zk-champ">
              <label htmlFor="vnd-marque">Marque</label>
              <input id="vnd-marque" required list="vnd-marques" autoComplete="off" maxLength={60} placeholder="Peugeot"
                value={vehicule.marque} onChange={majVehicule('marque')} onBlur={verifierMarque} />
              <datalist id="vnd-marques">{marques.map((m) => <option key={m} value={m} />)}</datalist>
              {correction && <small>{correction}</small>}
            </div>
            <div className="zk-champ">
              <label htmlFor="vnd-modele">Modèle</label>
              <input id="vnd-modele" required list="vnd-modeles" autoComplete="off" maxLength={60} placeholder="208"
                value={vehicule.modele} onChange={majVehicule('modele')} />
              <datalist id="vnd-modeles">{modeles.map((m) => <option key={m} value={joli(m)} />)}</datalist>
            </div>
            <div className="zk-champ">
              <label htmlFor="vnd-annee">Année</label>
              <input id="vnd-annee" required type="number" inputMode="numeric" min="1950" max="2100" placeholder="2017"
                value={vehicule.annee} onChange={majVehicule('annee')} />
            </div>
            <div className="zk-champ">
              <label htmlFor="vnd-km">Kilométrage</label>
              <input id="vnd-km" required type="number" inputMode="numeric" min="1" placeholder="98000"
                value={vehicule.kilometrage} onChange={majVehicule('kilometrage')} />
            </div>
            <div className="zk-champ">
              <label htmlFor="vnd-energie">Énergie</label>
              <select id="vnd-energie" value={vehicule.energie} onChange={majVehicule('energie')}>
                <option value="">Je ne sais pas</option>
                {ENERGIES.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div className="zk-champ">
              <label htmlFor="vnd-boite">Boîte</label>
              <select id="vnd-boite" value={vehicule.boite} onChange={majVehicule('boite')}>
                <option value="">Je ne sais pas</option>
                {BOITES.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" className="zk-bouton plein" disabled={occupe}>
            {occupe ? 'Estimation…' : 'Estimer ma voiture'}
          </button>
          <p className="zk-vnd-muet">Gratuit, sans compte, et le chiffre s’affiche avant qu’on vous demande quoi que ce soit.</p>
        </form>
      )}

      {etape === 2 && estimation && (
        <Estimation e={estimation} onSuite={() => setEtape(3)} onRetour={() => setEtape(1)} />
      )}

      {etape === 3 && (
        <form className="zk-carte zk-vnd-form" onSubmit={deposer}>
          <h2>Où vous joindre ?</h2>
          <div className="zk-vnd-grille">
            <div className="zk-champ">
              <label htmlFor="vnd-prenom">Prénom</label>
              <input id="vnd-prenom" required maxLength={60} autoComplete="given-name" value={contact.prenom} onChange={majContact('prenom')} />
            </div>
            <div className="zk-champ">
              <label htmlFor="vnd-tel">Téléphone</label>
              <input id="vnd-tel" required type="tel" autoComplete="tel" placeholder="06 12 34 56 78" value={contact.telephone} onChange={majContact('telephone')} />
            </div>
            <div className="zk-champ">
              <label htmlFor="vnd-email">Email</label>
              <input id="vnd-email" required type="email" autoComplete="email" value={contact.email} onChange={majContact('email')} />
            </div>
            <div className="zk-champ">
              <label htmlFor="vnd-cp">Code postal</label>
              <input id="vnd-cp" required inputMode="numeric" pattern="\d{5}" maxLength={5} placeholder="33000"
                autoComplete="postal-code" value={contact.code_postal} onChange={majContact('code_postal')} />
            </div>
          </div>

          <h3 className="zk-vnd-facultatif">Facultatif</h3>
          <div className="zk-vnd-grille">
            <div className="zk-champ">
              <label htmlFor="vnd-etat">État général</label>
              <select id="vnd-etat" value={contact.etat_general} onChange={majContact('etat_general')}>
                <option value="">Non précisé</option>
                {ETATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="zk-champ">
              <label htmlFor="vnd-souhaite">Prix souhaité (€)</label>
              <input id="vnd-souhaite" type="number" inputMode="numeric" min="1" value={contact.prix_souhaite} onChange={majContact('prix_souhaite')} />
              {ecartSouhaite != null && (
                <small>
                  {ecartSouhaite > 0 ? `${ecartSouhaite} % au-dessus` : `${Math.abs(ecartSouhaite)} % en dessous`} du prix
                  entre particuliers estimé
                </small>
              )}
            </div>
          </div>
          <div className="zk-champ">
            <label htmlFor="vnd-description">Décrivez votre voiture</label>
            <textarea
              id="vnd-description" rows={4} maxLength={2000} value={contact.description}
              onChange={majContact('description')}
              placeholder="Entretien suivi, distribution faite à 180 000 km, 4 pneus neufs, quelques rayures sur l'aile avant…"
            />
            <small>
              Ce texte est montré aux garages. Inutile d’y mettre vos coordonnées : téléphone, email et liens en sont
              retirés automatiquement, et les garages ne les obtiennent qu’après vous avoir demandé.
            </small>
          </div>
          <div className="zk-champ">
            <label htmlFor="vnd-photos">Photos (5 maximum)</label>
            <input id="vnd-photos" type="file" accept="image/*" multiple
              onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, 5))} />
            {photos.length > 0 && <small>{photos.length} photo{photos.length > 1 ? 's' : ''} jointe{photos.length > 1 ? 's' : ''}</small>}
          </div>

          {/* Honeypot : invisible, un humain ne le remplit jamais. */}
          <input type="text" className="zk-vnd-piege" tabIndex={-1} autoComplete="off" aria-hidden="true"
            value={contact.piege} onChange={majContact('piege')} />

          <label className="zk-vnd-consentement">
            <input type="checkbox" checked={consentement} onChange={(e) => setConsentement(e.target.checked)} required />
            <span>
              J’accepte que ZENKART transmette ma demande et mes coordonnées aux <b>garages partenaires</b> de ma région
              afin qu’ils me fassent une offre de rachat. ZENKART est rémunéré par ces garages.
            </span>
          </label>
          <p className="zk-vnd-muet">
            Vos coordonnées servent uniquement à vous rappeler et à transmettre votre demande. Vous pouvez les faire
            supprimer à tout moment en écrivant à <a href="mailto:privacy@zenkart.fr" className="zk-lien">privacy@zenkart.fr</a>.{' '}
            <Link to="/confidentialite" className="zk-lien">Politique de confidentialité</Link>.
          </p>

          <button type="submit" className="zk-bouton plein" disabled={occupe || !consentement}>
            {occupe ? 'Envoi…' : 'Recevoir des offres'}
          </button>
          <button type="button" className="zk-lien" onClick={() => setEtape(2)}>Revenir à l’estimation</button>
        </form>
      )}

      {etape === 4 && (
        <section className="zk-carte zk-vnd-confirmation">
          <h2>C’est enregistré</h2>
          <p className="zk-vnd-montant">Nous vous rappelons sous 24 h.</p>
          <p className="zk-vnd-muet">
            Nous vérifions votre annonce à la main avant de la transmettre aux garages de votre région : c’est ce qui
            fait que leurs offres sont sérieuses. {depot?.id ? `Votre demande porte le numéro ${depot.id}.` : ''}
          </p>
          {estimation?.niveau && (
            <p className="zk-vnd-muet">
              Pour mémoire : {nombre(estimation.prix_particulier)} € entre particuliers, reprise estimée entre{' '}
              {nombre(estimation.reprise_bas)} € et {nombre(estimation.reprise_haut)} €.
            </p>
          )}
        </section>
      )}

      <BandeauAffaires titre="Pendant ce temps, les bonnes affaires du moment" />
    </Page>
  );
}
